import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import Button from '@components/Button';
import FixedFooter from '@components/Layout/FixedFooter';
import { BILLING_TIME_MAP } from '@constants/billing';
import { RoutePath } from '@constants/routes';
import { handleApiError } from '@helpers/api';
import { accountActions, alertActions } from '@actions';
import { useTranslation, useYupValidationResolver, useQueryParams } from '@hooks';
import GTManager, { buildUserParams } from '@widgets/gtm';
import CurrentPlan from './CurrentPlan';
import DirectoriesAddOn from './DirectoriesAddOn';
import PulseAICredits from './PulseAICredits';

const Container = styled('div')({
    marginBottom: 65,
    '@media (max-width: 1024px)': {
        marginBottom: 80,
    },
});

const Footer = styled(FixedFooter)({
    '@media (max-width: 1024px)': {
        '& button': {
            width: '100%',
        },
    },
});

const BillingSettings = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const query = useQueryParams();
    const navigate = useNavigate();

    const account = useSelector(state => state.account?.account);
    const { usedCredits, availableCredits, additionalCreditsLeft } = useSelector(state => state.account);
    const user = useSelector(state => state.auth?.user);
    const isMultipleStripeAcc = account?.subscriptions.length > 1;

    const AutoPaySchema = yup.object().shape({
        autoPayCreditsLimit: yup.string().when('autoPayEnabled', {
            is: autoPayEnabled => autoPayEnabled,
            then: schema => schema.matches(/^\d+$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        autoPayCreditsAmount: yup.string().when('autoPayEnabled', {
            is: autoPayEnabled => autoPayEnabled,
            then: schema => schema.matches(/^\d+$/, 'validation.onlyNumbers').required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        autoPaySubscriptionId: yup.string().when('autoPayEnabled', {
            is: autoPayEnabled => autoPayEnabled && isMultipleStripeAcc,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
        autoPayEnabled: yup.boolean(),
    });

    const resolver = useYupValidationResolver(AutoPaySchema);

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            autoPayEnabled: false,
            autoPayCreditsLimit: `10`,
            autoPayCreditsAmount: `100`,
            autoPaySubscriptionId: '',
        },
    });

    useEffect(() => {
        const checkSubscriptionType = async () => {
            const sessionId = query.get('sessionId');
            if (!sessionId) {
                return;
            }
            const subscriptionInfo = await dispatch(accountActions.checkSubscriptionType(sessionId));
            if (subscriptionInfo) {
                const refreshedAccount = await dispatch(accountActions.get());
                const subscription = refreshedAccount?.subscriptions?.[0];
                if (refreshedAccount && subscription) {
                    GTManager.push('subscription-started', {
                        plan: subscription.billingPlanName ?? '',
                        amount: subscription.price ?? '',
                        period: subscription.productType === BILLING_TIME_MAP.ANNUAL ? 'year' : 'month',
                        currency: subscription.currency ?? '',
                        ...buildUserParams(user),
                        account_id: refreshedAccount.id ?? '',
                    });
                }
            }
            navigate(RoutePath.AccountSettingsBilling, { replace: true });
        };
        checkSubscriptionType();
        dispatch(accountActions.getPulseAiCredits());
    }, [query]);

    useEffect(() => {
        if (account) {
            setValue('autoPayEnabled', account?.autoPayEnabled, { shouldValidate: true });
            setValue('autoPayCreditsLimit', `${account?.autoPayCreditsLimit}` ?? 10, { shouldValidate: true });
            setValue('autoPayCreditsAmount', `${account?.autoPayCreditsAmount}` ?? 100, { shouldValidate: true });
            setValue('autoPaySubscriptionId', account?.autoPaySubscriptionId ?? '', { shouldValidate: true });
        }
    }, [account]);

    const onSubmit = async () => {
        const values = getValues();
        try {
            await dispatch(
                accountActions.updateAccount({
                    ...account,
                    ...values,
                    autoPayCreditsLimit: +values.autoPayCreditsLimit,
                    autoPayCreditsAmount: +values.autoPayCreditsAmount,
                }),
            );
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            reset(values);
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail');
        }
    };

    if (!account) return null;

    return (
        <Container>
            <CurrentPlan account={account} />
            <DirectoriesAddOn account={account} />
            <FormProvider {...{ control, setValue, watch }}>
                <PulseAICredits
                    account={account}
                    usedCredits={usedCredits}
                    availableCredits={availableCredits}
                    additionalCreditsLeft={additionalCreditsLeft}
                />
            </FormProvider>
            <Footer>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting || !isValid || !isDirty}
                >
                    {t('buttons.saveChanges')}
                </Button>
            </Footer>
        </Container>
    );
};

export default BillingSettings;
